import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    Select,
    MenuItem,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
    table: {
        marginTop: theme.spacing(1),
    },
    formControl: {
        minWidth: 200,
    },
}));

// Modal editor for the explicit cargo provider <-> product associations of a single product,
// each optionally tagged with a DeliveryMethod. Mirrors the purchase/sales group edit pattern:
// the product row in the parent table shows a read-only summary/expand view, while all
// add/edit/delete actions for its associations happen here.
export default function CargoProductProviderEditor({
    open,
    product,
    providers,
    deliveryMethods,
    associations,
    onAdd,
    onUpdateDeliveryMethod,
    onDelete,
    onClose,
}) {
    const classes = useStyles();
    const { t } = useTranslation();

    const [draft, setDraft] = useState({ providerId: '', deliveryMethodId: '' });

    useEffect(() => {
        if (open) setDraft({ providerId: '', deliveryMethodId: '' });
    }, [open, product]);

    const usedProviderIds = (associations || []).map((a) => a.cargoProviderId);
    const availableProviders = (providers || []).filter((p) => !usedProviderIds.includes(p.clusterId));

    const getProviderName = (id) => {
        const p = (providers || []).find((prov) => prov.clusterId === id);
        return p ? p.name : '';
    };

    const handleAdd = () => {
        if (!draft.providerId) return;
        onAdd(draft.providerId, draft.deliveryMethodId || null);
        setDraft({ providerId: '', deliveryMethodId: '' });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('cargo.editProductProviders', { type: product ? product.type : '' })}</DialogTitle>
            <DialogContent>
                <Table className={classes.table} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('cargo.provider')}</TableCell>
                            <TableCell>{t('cargo.deliveryMethod')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(associations || []).map((a) => (
                            <TableRow key={a.clusterId}>
                                <TableCell>{getProviderName(a.cargoProviderId)}</TableCell>
                                <TableCell>
                                    <FormControl size="small" className={classes.formControl}>
                                        <Select
                                            displayEmpty
                                            value={a.deliveryMethodId || ''}
                                            onChange={(e) => onUpdateDeliveryMethod(a, e.target.value)}>
                                            <MenuItem value="">{t('cargo.noDeliveryMethod')}</MenuItem>
                                            {(deliveryMethods || []).map((d) => (
                                                <MenuItem key={d.id} value={d.id}>
                                                    {d.value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        size="small"
                                        aria-label={t('cargo.confirmDeleteProviderProduct', {
                                            name: getProviderName(a.cargoProviderId),
                                        })}
                                        onClick={() => onDelete(a)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell>
                                <FormControl size="small" className={classes.formControl}>
                                    <Select
                                        displayEmpty
                                        value={draft.providerId || ''}
                                        onChange={(e) => setDraft((prev) => ({ ...prev, providerId: e.target.value }))}>
                                        <MenuItem value="">{t('cargo.selectProvider')}</MenuItem>
                                        {availableProviders.map((prov) => (
                                            <MenuItem key={prov.clusterId} value={prov.clusterId}>
                                                {prov.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell>
                                <FormControl size="small" className={classes.formControl}>
                                    <Select
                                        displayEmpty
                                        value={draft.deliveryMethodId || ''}
                                        onChange={(e) =>
                                            setDraft((prev) => ({ ...prev, deliveryMethodId: e.target.value }))
                                        }>
                                        <MenuItem value="">{t('cargo.noDeliveryMethod')}</MenuItem>
                                        {(deliveryMethods || []).map((d) => (
                                            <MenuItem key={d.id} value={d.id}>
                                                {d.value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell align="right">
                                <IconButton
                                    size="small"
                                    aria-label={t('cargo.addProviderAssociation')}
                                    disabled={!draft.providerId}
                                    onClick={handleAdd}>
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained" color="primary">
                    {t('buttons.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
